
import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { formatDateTime } from '../utils/dateUtils';



interface Deposit {
  uid: string;
  date: string;
  txtid: string;
  amount: number;
  coin: string;
  name: string;
  parent_role?: string;
  parent_name?: string;
}

const Deposit: React.FC = () => {
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [adminFilter, setAdminFilter] = useState<string>("all");
  const [managerFilter, setManagerFilter] = useState<string>("all");
  const [affiliateFilter, setAffiliateFilter] = useState<string>("all");

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  useEffect(() => {
    const fetchDepositData = async () => {
      try {
        const response = await apiClient.get(`/getDepositData`);
        const data = response.data;

        if (Array.isArray(data)) {
          const reversedData = data.reverse();
          // Normalize direct/admin users' parent properties if not set
          const normalized = reversedData.map((d: any) => {
            if (!d.parent_role) {
              return { ...d, parent_role: 'admin', parent_name: d.parent_name || 'Super Admin' };
            }
            return d;
          });
          setDeposits(normalized);
        } else {
          console.error('Unexpected response structure:', data);
          setDeposits([]);
        }
      } catch (error) {
        console.error('Failed to fetch deposit data:', error);
        setDeposits([]);
      }
    };

    fetchDepositData();
  }, []);

  let filteredDepositData = searchTerm
    ? deposits.filter((deposit) => String(deposit.uid).includes(searchTerm) || String(deposit.name).toLowerCase().includes(searchTerm.toLowerCase()))
    : deposits;

  if (adminFilter !== "all") {
    filteredDepositData = filteredDepositData.filter(d => d.parent_role === 'admin' && d.parent_name === adminFilter);
  }
  if (managerFilter !== "all") {
    filteredDepositData = filteredDepositData.filter(d => d.parent_role === 'manager' && d.parent_name === managerFilter);
  }
  if (affiliateFilter !== "all") {
    filteredDepositData = filteredDepositData.filter(d => d.parent_role === 'affiliate' && d.parent_name === affiliateFilter);
  }

  const admins = Array.from(new Set(deposits.filter(d => d.parent_role === 'admin').map(d => d.parent_name).filter(Boolean)));
  const managers = Array.from(new Set(deposits.filter(d => d.parent_role === 'manager').map(d => d.parent_name).filter(Boolean)));
  const affiliates = Array.from(new Set(deposits.filter(d => d.parent_role === 'affiliate').map(d => d.parent_name).filter(Boolean)));

  return (
    <div className='ml-0 lg:ml-[256px]'>
      <div className="mb-2 flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          placeholder="Search by name or UID"
          value={searchTerm}
          onChange={handleSearchChange}
          className="p-1 border rounded w-full sm:w-1/4"
        />
        <select 
          value={adminFilter} 
          onChange={(e) => { setAdminFilter(e.target.value); setManagerFilter("all"); setAffiliateFilter("all"); }}
          className="p-1 border rounded w-full sm:w-1/4"
        >
          <option value="all">All Admins (Direct Users)</option>
          {admins.map(adm => <option key={adm as string} value={adm as string}>{adm as string}</option>)}
        </select>
        <select 
          value={managerFilter} 
          onChange={(e) => { setManagerFilter(e.target.value); setAdminFilter("all"); setAffiliateFilter("all"); }}
          className="p-1 border rounded w-full sm:w-1/4"
        >
          <option value="all">All Managers</option>
          {managers.map(m => <option key={m as string} value={m as string}>{m as string}</option>)}
        </select>
        <select 
          value={affiliateFilter} 
          onChange={(e) => { setAffiliateFilter(e.target.value); setAdminFilter("all"); setManagerFilter("all"); }}
          className="p-1 border rounded w-full sm:w-1/4"
        >
          <option value="all">All Affiliates</option>
          {affiliates.map(a => <option key={a as string} value={a as string}>{a as string}</option>)}
        </select>
      </div>
      <div className="overflow-hidden">
        <table className="min-w-full table-fixed">
          <thead>
            <tr>
              <th className="py-1 px-2 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">S.No.</th>
              <th className="py-1 px-2 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">UID</th>
              <th className="py-1 px-2 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
              <th className="py-1 px-2 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Manager/Affiliate</th>
              <th className="py-1 px-2 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
              <th className="py-1 px-2 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Transaction Id</th>
              <th className="py-1 px-2 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
              <th className="py-1 px-2 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Coin</th>
            </tr>
          </thead>
          <tbody>
            {filteredDepositData.map((deposit, index: number) => (
              <tr key={index + 1}>
                <td className="py-1 px-2 border-b border-gray-200">{index + 1}</td>
                <td className="py-1 px-2 border-b border-gray-200">{deposit.uid}</td>
                <td className="py-1 px-2 border-b border-gray-200">{deposit.name}</td>
                <td className="py-1 px-2 border-b border-gray-200 text-xs">
                  {deposit.parent_role ? <span className="capitalize">{deposit.parent_role}: {deposit.parent_name}</span> : <span className="text-gray-400">Direct User</span>}
                </td>
                <td className="py-1 px-2 border-b border-gray-200">{formatDateTime(deposit.date)}</td>
                <td className="py-1 px-2 border-b border-gray-200">{deposit.txtid}</td>
                <td className="py-1 px-2 border-b border-gray-200">{deposit.amount}</td>
                <td className="py-1 px-2 border-b border-gray-200">{deposit.coin}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Deposit;
